import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import storage from "web/script/utils/storage";
import logging from "web/script/utils/logging";
import url from "web/script/utils/url";
import uxscoreTemplate from "templates/modules/ux-score.jinja";
import baustein from "baustein";
import { gettext } from "web/script/modules/django-i18n";

// Time to wait until showing the questionnaire again- 20 days (in ms)
const completionResetTime = 60 * 60 * 24 * 20 * 1000;

export default baustein.register("ux-score", {
    defaultOptions: {
        score: null,
        alwaysShow: false,
        hiddenCloseBtn: false,
        type: "web",
        surveyId: null,
    },
    template: uxscoreTemplate,

    init() {
        this.score = this.options.score;
    },

    getInitialRenderContext() {
        // Based on your experiences so far, would you be happy to use Lyst again?
        let title = gettext("ux_score.title");
        // No, definitely not
        let minScoreLabel = gettext("ux_score.min_score_label");
        // Yes, definitely
        let maxScoreLabel = gettext("ux_score.max_score_label");
        // Thank you for your feedback. What puts you off using Lyst again?
        let lowScoreAdditionalQuestion = gettext("ux_score.low_score_additional_question");
        // Thank you for your feedback. What could we change to make Lyst better for you?
        let midScoreAdditionalQuestion = gettext("ux_score.mid_score_additional_question");
        // Thank you for your feedback. What is the best thing about using Lyst?
        let highScoreAdditionalQuestion = gettext("ux_score.high_score_additional_question");

        if (this.options.type === "email") {
            // How do you feel about the emails you receive from Lyst?
            title = gettext("ux_score_email.title");
            // I hate them
            minScoreLabel = gettext("ux_score_email.min_score_label");
            // I love them
            maxScoreLabel = gettext("ux_score_email.max_score_label");
            // Thank you for your feedback. What makes you hate the emails you receive from Lyst?
            lowScoreAdditionalQuestion = gettext("ux_score_email.low_score_additional_question");
            // Thank you for your feedback. How could we improve the emails you receive from Lyst?
            midScoreAdditionalQuestion = gettext("ux_score_email.mid_score_additional_question");
            // Thank you for your feedback. What makes your love the emails you receive from Lyst?
            highScoreAdditionalQuestion = gettext("ux_score_email.high_score_additional_question");
        }

        return {
            title,
            minScoreLabel,
            maxScoreLabel,
            lowScoreAdditionalQuestion,
            midScoreAdditionalQuestion,
            highScoreAdditionalQuestion,
            // Submit
            submitBtnText: gettext("oos_product.inventory_request_form_submit_btn_text"),
            // Thank you for your feedback.
            submittedTitle: gettext("ux_score.submitted_title"),
            // Your input helps us to improve Lyst.
            submittedSubtitle: gettext("ux_score.submitted_subtitle"),
            currentScore: this.score,
            hiddenCloseBtn: this.options.hiddenCloseBtn,
            scoreRange: _.range(1, 8),
        };
    },

    setupEvents(add) {
        add("click", ".ux-score__close", this.onCloseClick);
        add("click", ".ux-score__score", this.onScoreClick);
        add("submit", ".ux-score__feedback-form", this.onFeedbackSubmit);
    },

    shouldDisplay() {
        if (this.options.alwaysShow) {
            return true;
        }

        const completionTimestamp = storage.get("ux-score");
        const hasBeenCompleted = completionTimestamp
            ? Date.now() - completionTimestamp <= completionResetTime
            : false;
        const pageViewCount = analytics.getPageViewsCount();
        const queryParams = url.toURLSearchParams(window.location.search);

        const shouldShow =
            queryParams.get("uxscore") === "force" || (!hasBeenCompleted && pageViewCount >= 8);

        if (shouldShow) {
            analytics.event("ux_score", "show_ux_score", null, true);
        }

        return shouldShow;
    },

    onInsert() {
        this.setScore(this.score);
        if (!this.shouldDisplay()) {
            this.destroy();
            return;
        }

        // This is a cheeky way to only render the component client side
        // without having to put it in the server side response. #baustein
        this.render();

        if (this.score && this.options.alwaysShow) {
            const additionalQuestion = this.getAdditionalQuestion();
            if (additionalQuestion) {
                this.showQuestion(additionalQuestion);
            }
        }

        this.el.classList.remove("hidden");
    },

    trackUXScore(additionalAnswer) {
        const scoreQuestion = this.el.querySelector(".ux-score__question").textContent;
        const additionalQuestion = this.getAdditionalQuestion();
        const additionalQuestionVal = (additionalQuestion && additionalQuestion.textContent) || "";

        if (this.options.type === "email") {
            if (!this.options.surveyId) {
                throw new Error("No surveyId was provided");
            }
            const data = {
                _type: "survey_responses",
                _version: "0.0.1",
                survey_id: this.options.surveyId,
                response_json: JSON.stringify({
                    score_question: scoreQuestion,
                    max_score: 7,
                    additional_question: additionalQuestionVal,
                    additional_answer: additionalAnswer || "",
                    score_answer: this.score,
                }),
            };
            return analytics.trackWithMetaData("survey_response", data);
        }
        return analytics.trackUXScore({
            scoreQuestion: scoreQuestion,
            maxScore: 7,
            additionalQuestion: additionalQuestionVal,
            additionalAnswer: additionalAnswer || "",
            scoreAnswer: this.score,
        });
    },

    setComplete() {
        const timeNow = Date.now();
        storage.set("ux-score", timeNow);
    },

    onCloseClick() {
        this.setComplete();
        this.destroy();
    },

    onScoreClick(event) {
        const clickedScore = event.target && Number(event.target.dataset.score);

        if (!clickedScore) {
            return;
        }

        this.setScore(clickedScore);

        this.trackUXScore();
        this.setComplete();

        const additionalQuestion = this.getAdditionalQuestion();
        if (additionalQuestion) {
            this.showQuestion(additionalQuestion);
        }
    },

    setScore(score) {
        this.score = score;
        Array.from(this.el.querySelectorAll(".ux-score__score")).forEach((scoreEl) => {
            if (Number(scoreEl.dataset.score) === this.score) {
                if (!scoreEl.classList.contains("ux-score__score--active")) {
                    scoreEl.classList.add("ux-score__score--active");
                }
            } else {
                scoreEl.classList.remove("ux-score__score--active");
            }
        });
    },

    getAdditionalQuestion() {
        const additionalQuestions = Array.from(
            this.el.querySelectorAll(".ux-score__additional-question")
        );
        const questionToShow = additionalQuestions.filter((q) => {
            const minScore = Number(q.dataset.minScore);
            const maxScore = Number(q.dataset.maxScore);

            return minScore <= this.score && this.score <= maxScore;
        })[0];

        return questionToShow || null;
    },

    showQuestion(question) {
        this.el.querySelector(".ux-score__feedback").classList.remove("hidden");
        const additionalQuestions = Array.from(
            this.el.querySelectorAll(".ux-score__additional-question")
        );
        additionalQuestions.forEach((q) => q.classList.add("hidden"));
        question.classList.remove("hidden");
    },

    onFeedbackSubmit(event) {
        event.preventDefault();

        const form = event.currentTarget;
        const input = form.querySelector(".ux-score__submit-feedback");
        const feedbackTextarea = form.querySelector("textarea[name=feedback]");
        const text = feedbackTextarea && feedbackTextarea.value;

        if (!this.score || !text) {
            form.querySelector(".nps-survey-error").classList.remove("hidden");
            input.disabled = false;
        }

        input.disabled = true;
        input.classList.add("btn--disabled");

        this.trackUXScore(text)
            .then(this.onSubmitSuccess.bind(this))
            .catch(this.onSubmitError.bind(this));
        this.setComplete();
    },

    onSubmitSuccess() {
        this.el.querySelector(".ux-score__question-wrapper").classList.add("hidden");
        this.el.querySelector(".ux-score__feedback").classList.add("hidden");
        this.el.querySelector(".ux-score__thanks").classList.remove("hidden");

        window.scrollTo(0, 0);
    },

    onSubmitError() {
        logging.error("An error happened while trying to submit the UXScore component feedback.");

        this.destroy();
    },
});
